// The 30-day trial (WI15): every lead from the site, their answers, plan, stage and dates, and the mail xPay sent
// the members@ mailbox about them, with a message ready to send from the Ops number by hand.
import { useState } from "react";

import { api, send } from "./api";
import { day, phone, when } from "./format";
import { useAct } from "./Members";
import { usePoll } from "./Pages";
import type { TrialDetail, TrialMail, Trials as TrialsList } from "./types";

const STAGES: Record<string, string> = {
  lead: "Lead",
  waitlist: "Waitlist",
  plan_chosen: "Plan chosen",
  card_pending: "Card pending",
  trial: "On trial",
  member: "Paying member",
  charge_failed: "Charge failed",
  paused: "Paused",
  cancelled: "Cancelled",
};
const PLANS: Record<string, string> = { quarterly: "$147 every 3 months", monthly: "$65 a month" };
const ANSWERS: Record<string, string> = {
  name: "Name",
  email: "Email",
  city: "Parents' city",
  cityServed: "City served",
  ownCity: "Lives in",
  categories: "Would use Niro for",
  setup: "Family group or 1:1",
  taskText: "In their words",
  intent: "Asked about",
  page: "Page",
  utm_source: "Source",
  utm_campaign: "Campaign",
};

function Mail({ mail, onDone }: { mail: TrialMail; onDone: (id: string) => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="stack">
      <p>
        <strong>{mail.subject || "(no subject)"}</strong>{" "}
        <span className="muted">
          {mail.sender} · {when(mail.received_at)}
        </span>
      </p>
      <p className="muted">{mail.snippet}</p>
      {mail.message && (
        <>
          <textarea readOnly rows={3} value={mail.message} />
          <div className="actions">
            <button
              className="secondary"
              onClick={async () => {
                await navigator.clipboard.writeText(mail.message!);
                setCopied(true);
              }}
            >
              {copied ? "Copied" : "Copy"}
            </button>
            {mail.whatsapp && (
              <a href={mail.whatsapp} target="_blank" rel="noreferrer">
                Open in WhatsApp
              </a>
            )}
            {!mail.done_at && <button onClick={() => onDone(mail.id)}>Mark done</button>}
          </div>
        </>
      )}
      {!mail.message && !mail.done_at && (
        <div className="actions">
          <button onClick={() => onDone(mail.id)}>Mark done</button>
        </div>
      )}
      {mail.done_at && <p className="muted">Done {when(mail.done_at)}</p>}
    </div>
  );
}

export function TrialPage({ id, onBack }: { id: string; onBack: () => void }) {
  const { data, error, reload } = usePoll(() => api<TrialDetail>(`/api/trials/${id}`), 30000);
  const { act, problem } = useAct(reload);
  if (error) {
    return <p className="note warn">{error}</p>;
  }
  if (!data) {
    return <p className="muted">Loading…</p>;
  }
  return (
    <div className="stack wide">
      <button className="secondary" onClick={onBack}>
        Back to trials
      </button>
      {problem && <p className="note warn">{problem}</p>}
      <h2>
        {data.ref} {data.answers.name ? `· ${data.answers.name}` : ""}
      </h2>
      <p>
        {phone(data.phone)} · {STAGES[data.stage] ?? data.stage}
        {data.plan ? ` · ${PLANS[data.plan] ?? data.plan}` : ""}
        {data.trial_status ? ` · xPay says ${data.trial_status}` : ""}
      </p>
      {data.trial_start && (
        <p>
          Free from {day(data.trial_start)} to {day(data.trial_end)}
          {data.next_payment_at ? `; xPay's next charge ${day(data.next_payment_at)}` : ""}
          {data.converted_at ? `; first charged ${day(data.converted_at)}` : ""}
          {data.cancelled_at ? `; cancelled ${day(data.cancelled_at)}` : ""}
        </p>
      )}
      <table className="list">
        <tbody>
          {Object.entries(data.answers).map(([key, value]) => (
            <tr key={key}>
              <th>{ANSWERS[key] ?? key}</th>
              <td>{String(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h2>xPay's mail</h2>
      {data.mail.length === 0 ? (
        <p className="muted">None yet.</p>
      ) : (
        data.mail.map((m) => <Mail key={m.id} mail={m} onDone={(mail) => act(() => send(`/api/trial-mail/${mail}/done`))} />)
      )}
      {data.checkouts.length > 0 && (
        <>
          <h2>Checkouts</h2>
          <table className="list">
            <thead>
              <tr>
                <th>Plan</th>
                <th>xPay says</th>
                <th>Made</th>
              </tr>
            </thead>
            <tbody>
              {data.checkouts.map((c) => (
                <tr key={c.created_at}>
                  <td>{PLANS[c.plan] ?? c.plan}</td>
                  <td>{c.status}</td>
                  <td>{when(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

export function Trials({ onOpen, admin }: { onOpen: (id: string) => void; admin: boolean }) {
  const { data, error, reload } = usePoll(() => api<TrialsList>("/api/trials"), 30000);
  const { act, problem } = useAct(reload);
  if (error) {
    return <p className="note warn">{error}</p>;
  }
  if (!data) {
    return <p className="muted">Loading…</p>;
  }
  const stuck = data.outbox.filter((o) => o.attempts >= 3);
  return (
    <div className="stack wide">
      {problem && <p className="note warn">{problem}</p>}
      {stuck.length > 0 && (
        <p className="note warn">
          Not reaching the sheet or Meta yet: {stuck.map((o) => `${o.n} ${o.kind}`).join(", ")}. Nothing is lost: they keep
          trying.
        </p>
      )}
      {admin && (
        <div className="actions">
          <button className="secondary" onClick={() => act(() => send("/api/trials/ops-today"))}>
            Rewrite the Ops Today tab now
          </button>
        </div>
      )}
      {data.unmatched_mail.length > 0 && (
        <>
          <h2>Mail with no lead</h2>
          {data.unmatched_mail.map((m) => (
            <Mail key={m.id} mail={m} onDone={(mail) => act(() => send(`/api/trial-mail/${mail}/done`))} />
          ))}
        </>
      )}
      <h2>Leads</h2>
      {data.leads.length === 0 ? (
        <p className="muted">None yet.</p>
      ) : (
        <table className="list">
          <thead>
            <tr>
              <th>Ref</th>
              <th>Name</th>
              <th>Number</th>
              <th>Parents' city</th>
              <th>Would use Niro for</th>
              <th>Setup</th>
              <th>Plan</th>
              <th>Stage</th>
              <th>Free until</th>
              <th>Mail</th>
              <th>Came</th>
            </tr>
          </thead>
          <tbody>
            {data.leads.map((l) => (
              <tr key={l.id} onClick={() => onOpen(l.id)}>
                <td>{l.ref}</td>
                <td>{String(l.answers.name ?? "")}</td>
                <td>{phone(l.phone)}</td>
                <td>{String(l.answers.city ?? "")}</td>
                <td>{String(l.answers.categories ?? "")}</td>
                <td>{String(l.answers.setup ?? "")}</td>
                <td>{l.plan ? (PLANS[l.plan] ?? l.plan) : ""}</td>
                <td>{STAGES[l.stage] ?? l.stage}</td>
                <td>{day(l.trial_end)}</td>
                <td>{l.open_mail > 0 ? l.open_mail : ""}</td>
                <td>{when(l.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
